import React from "react";
import { defaultServiceContext } from "../contexts";
import {
    FormField,
    ItemDropdown,
    NoInputTimeoutOverride
} from "../form_components";
import Input from "../form_components/Input";
import ItemList from "../form_components/ItemList";
import Locale from "../form_components/Locale";
import FillerPrompts from "../form_components/FillerPrompts";
import LiveAgent from "../form_components/LiveAgent";
import CCXML from "../form_components/CCXML";
import ExceptionHandling from "../form_components/ExceptionHandling";
import LoopHandling from "../form_components/LoopHandling";
import ErrorHandling from "../form_components/ErrorHandling";
import Textarea from "../form_components/Textarea";
import { DesignRootFeatureFlags } from "../page-components";
import { SettingsHauPage } from "../pages/app/settings/hau";
import { FeatureFlag } from "../services";
import SettingsNav from "../widgets/components/SettingsNav";
import ExportWidget from "../widgets/ExportWidget";
import AppProperties from "../form_components/AppProperties";
import IntentHandling from "../form_components/IntentHandling";
import CallEncryption from "../form_components/CallEncryption";
import About from "../form_components/About";
import Utility from "../utilities/Utility";
import CoMoWizardConfig from "../form_components/CoMoWizardConfig";
import DesignerSetting from "../DesignerSetting";
import { DesignRuntimeEngine } from "../types";
import { SwitchWrapper } from "../form_components/switch-wrapper";

/**
 * @import { Design } from "../types";
 * @import { Option } from "../utilities/components";
 */

/** @type {Option<DesignRuntimeEngine>[]} */
const runtimeEngineOptions = [
    {
        label: 'GraalVM',
        value: DesignRuntimeEngine.graalVm
    },
    {
        label: 'Nashorn',
        value: DesignRuntimeEngine.nashorn
    }
];

const {
    globalModalsService
} = defaultServiceContext;

/**
 * @typedef SettingWidgetProps
 * @type {object}
 * @property {string} [csvDataKey="none"]
 * @property {string} [graphQLKey="none"]
 * @property {Record<string, string>} labels
 * @property {Design} serialized
 */

/**
 * @extends React.Component<SettingWidgetProps>;
 */
export default class SettingWidget extends React.Component {
    /**
     * Intents page
     * @module SettingsWidget
     * @param {SettingWidgetProps} props React props
     */
    constructor(props) {
        super(props);

        const { serialized } = props;
        const {
            rootSettings: { runtimeEngine = DesignRuntimeEngine.nashorn }
        } = serialized;
        this.state = {
            latestVersion: null,
            referenceablePrompts: props.serialized.components.objects.filter(o => o._class === "PromptNodeModel" && o.props.config.isReferenceable),
            runtimeEngine,
            csvData: props.serialized.csvData,
            graphQLSchemas: props.serialized.graphQLSchemas,
            graphQLKey: props.graphQLKey ? props.graphQLKey : "none",
            csvDataKey:  props.csvDataKey ? props.csvDataKey : "none",
            languages: props.serialized.languages,
            voice: props.serialized.voice,
            description: props.serialized.description,
            validationErrorMessage: "Application name already exists. Please try a different name." // default value
        };
    }

    /**
     * Set config proxy
     * @param {string} key
     * @param {*} value
     */
    setConfig(key, value) {
        const basicPrefix = "basic-";
        const isBaseKey = key.startsWith(basicPrefix);
        key = (!isBaseKey) ? key : (
            key.substring(basicPrefix.length, key.length)
        );
        if (value !== this.props.serialized[key]) {
            this.setState({ [key]: value });
        }

        window.apm.setBasic(key, value);
    }


    /**
     * Get config proxy
     * @param {string} key
     * @returns {*}
     */
    getConfig(key) {
        var basicPrefix = "basic-";
        if (key.startsWith(basicPrefix)) {
            return this.props.serialized[key.substring(basicPrefix.length, key.length)];
        }
        return null;
    }

    getReferenceablePrompts() {
        return window.apm.serialized.components.objects.filter(o => o._class === "PromptNodeModel" && o.props.config.isReferenceable)
    }

    componentWillReceiveProps(nextProps) {
        if (window.apm.latestVersion !== this.state.latestVersion) {
            this.setState({ 'latestVersion': window.apm.latestVersion });
        }
        if(nextProps.referenceablePrompts && nextProps.referenceablePrompts !== this.state.referenceablePrompts) {
            this.setState({referenceablePrompts: nextProps.referenceablePrompts});
        }
        if(nextProps.serialized.description && nextProps.serialized.description !== this.state.description) {
            this.setState({description: nextProps.serialized.description});
        }
        if(nextProps.serialized.csvData && nextProps.serialized.csvData !== this.state.csvData) {
            this.setState({csvData: nextProps.serialized.csvData});
        }
        if(nextProps.serialized.graphQLSchemas && nextProps.serialized.graphQLSchemas !== this.state.graphQLSchemas) {
            this.setState({graphQLSchemas: nextProps.serialized.graphQLSchemas});
        }
        if(nextProps.csvDataKey && nextProps.csvDataKey !== this.state.csvDataKey) {
            this.setState({csvDataKey: nextProps.csvDataKey});
        }
        if(nextProps.graphQLKey && nextProps.graphQLKey !== this.state.graphQLKey) {
            this.setState({graphQLKey: nextProps.graphQLKey});
        }
        if(nextProps.serialized.languages && nextProps.serialized.languages !== this.state.languages) {
            this.setState({languages: nextProps.serialized.languages});
        }
        if(nextProps.serialized.voice && nextProps.serialized.voice !== this.state.voice) {
            this.setState({voice: nextProps.serialized.voice});
        }
    }

    getIntentHandlingProps(){
        const exemptedIntents = ['YES', 'NO', 'WAIT', 'BACK_FROM_WAIT', 'DTMF'];
        const inARowLeft = [];
        const inARowRight = [];
        const xAndOutLeft = [];
        const xAndOutRight = [];

        const inARowExcluded = Utility.getInARowExcludedIntentIds(this.props.serialized.components.intents);
        const xAndOutExcluded = Utility.getXAndOutExcludedIntentIds(this.props.serialized.components.intents);
        const inARowLimit = this.props.serialized.intentHandling.inARowLimit;
        const xAndOutLimit = this.props.serialized.intentHandling.xAndOutLimit;

        this.props.serialized.components.intents.filter(int => exemptedIntents.indexOf(int.name) === -1)
            .filter(int => !int.isHidden)
            .forEach(intent => {
                if (inARowExcluded.some(iar => iar === intent.id)) { 
                    inARowRight.push({
                        id: intent.id,
                        value: intent.name
                    });
                } else {
                    inARowLeft.push({
                        id: intent.id,
                        value: intent.name
                    });
                }
                if (xAndOutExcluded.some(iar => iar === intent.id)) {
                    xAndOutRight.push({
                        id: intent.id,
                        value: intent.name
                    });
                } else {
                    xAndOutLeft.push({
                        id: intent.id,
                        value: intent.name
                    });
                }
            });
      
        return {    
            exemptedIntents, 
            inARowLeft, 
            inARowRight, 
            xAndOutLeft, 
            xAndOutRight,
            inARowLimit,
            xAndOutLimit
        }
    }

    /**
     * React render
     * @returns {XML}
     */
    render() {
        // TODO: Feature flag 894 begins
        const inventoryEnabled = Utility.getCookieBool(FeatureFlag.inventory);
        // TODO: Feature flag 894 ends
        const intentHandlingProps = this.getIntentHandlingProps();
        const isClientView = DesignerSetting.s.is_public === "true";
        //Feature flag 1577
        const wizardConfig= Utility.getCookieBool(FeatureFlag.wizardConfig);
        const isCommonModule = Utility.getIsCommonModule(this.props.serialized);
        const {
            rootSettings
        } = this.props.serialized;
        const {
            runtimeEngine
        } = this.state;

        return (
            <div className="container-fluid">
                <SettingsNav
                    serialized={this.props.serialized}
                    labels={this.props.labels} />

                <div className="tab-content col-md-8 col-md-offset-2">

                    {!isCommonModule &&
                        <div id="basic" className="tab-pane fade in active ">
                            <div className="row">
                                <div className="col-md-12">
                                    <label>{this.props.labels.application_basic}</label>
                                </div>

                                {(this.props.serialized.archetypeVersion !== undefined) ? (
                                    <div className="col-md-6">
                                        <Input
                                            label={this.props.labels.archetype_version}
                                            tooltip="The version of the Archetype application used to provide the application's pre-configured components."
                                            context={this} valKey={"basic-archetypeVersion"}
                                            value={this.props.serialized.archetypeVersion}
                                            disabled={true}
                                        />
                                    </div>
                                ) : null}

                                <div className="col-md-6">
                                    <FormField
                                        label="Runtime Engine"
                                        tooltip="JavaScript runtime engine used when PE runs."
                                    >
                                        <ItemDropdown
                                            items={runtimeEngineOptions}
                                            onChange={async (newRuntimeEngine) => {
                                                if (newRuntimeEngine === runtimeEngine) {
                                                    return;
                                                }

                                                const {
                                                    deprecatedNashorn,
                                                    wishToProceed
                                                } = window.l.popup;
                                                const proceed =
                                                  newRuntimeEngine === DesignRuntimeEngine.nashorn
                                                    ? await globalModalsService.openConfirmationModal({
                                                        children: (
                                                          <div className="box content">
                                                            <span style={{ color: 'var(--color-error)'}}>{deprecatedNashorn}</span>
                                                            <span>{wishToProceed}</span>
                                                          </div>
                                                        )
                                                      })
                                                    : true;
                                                if (!proceed) {
                                                    return;
                                                }
                                                this.setState({ runtimeEngine: newRuntimeEngine });
                                                window.apm.setBasic('rootSettings.runtimeEngine', newRuntimeEngine);
                                            }}
                                            required
                                            value={runtimeEngine}
                                        />
                                    </FormField>
                                </div>
                            </div>
                            <hr />
                            <div className="row">
                                <div className="col-md-6">
                                    <Textarea 
                                        label={this.props.labels.description.toUpperCase()} 
                                        tooltip="Write a short description of the application's purpose."
                                        context={this}
                                        valKey={"basic-description"} 
                                        value={this.state.description} />
                                </div>
                            </div>
                            <hr />

                            {(!isClientView) ? null : (
                                // Display here because client view does not get the save page.
                                <ExportWidget />
                            )}

                            <div className="row">
                            <div className="col-md-12">
                                    <label>{"Global Error Handling".toUpperCase()}</label>
                                </div>
                                <div className="col-md-12">
                                    <ErrorHandling context={this} valKey={"basic-globalErrorHandling"} value={this.props.serialized.globalErrorHandling} />
                                </div>
                            </div>
                            <hr />

                            <div className="row">
                                <div className="col-md-12">
                                    <label>{this.props.labels.exception_handling}</label>
                                </div>
                                <div className="col-md-12">
                                    <ExceptionHandling context={this} valKey={"basic-globalExceptionHandling"} value={this.props.serialized.globalExceptionHandling}/>
                                </div>
                            </div>
                            <hr />
                            <div className="row">
                                <div className="col-md-12">
                                    <label>LOOP HANDLING</label>
                                </div>
                                <div className="col-md-12">
                                    <LoopHandling 
                                        context={this} 
                                        valKey={"basic-globalExceptionHandling"} 
                                        value={this.props.serialized.globalLoopHandling}
                                        serialized={this.props.serialized}
                                        />
                                </div>
                            </div>
                            <hr />
                            <div className="row">
                                <div className="col-md-12">
                                    <label>INTENT HANDLING</label>
                                </div>
                                <div className="col-md-12">
                                    <IntentHandling 
                                        serialized={this.props.serialized}
                                        {...intentHandlingProps}
                                    />
                                </div>  
                            </div>
                            <hr />
                            <DesignRootFeatureFlags />
                            <hr />
                        <div className="row">
                            <div className="col-md-12">
                                <label>{"Full Call Encryption".toUpperCase()}</label>
                                <CallEncryption callEncryption={window.apm.serialized.appProperties.find(p => p.type === "callEncryption")} />
                            </div>
                        </div>
                        <hr />
                        <div className="row">
                            <div className="col-md-12">
                                <label
                                    data-tip={window.l.property?.["noInputTimeout_tooltip"]}
                                >
                                        {window.l.property.noInputTimeout_global.toUpperCase()}
                                        <i className="fe icon-info"/>
                                </label>
                                <NoInputTimeoutOverride
                                    onValueChange={(timeoutOverrides) => {
                                        this.setConfig('rootSettings.noInputTimeoutOverride', timeoutOverrides);
                                    }}
                                    value={rootSettings.noInputTimeoutOverride}
                                />
                            </div>
                        </div>
                        <hr/>
                        <div className="row">
                            <div className="col-md-12">
                                <label
                                    data-tip="JavaScript code blocks available for reuse by Custom Script objects. Click + to add a new function."
                                >
                                    {this.props.labels.reusable_functions.toUpperCase()}
                                    <i className="fe icon-info"/>
                                </label>
                            </div>
                            <div className="col-md-12">
                                <ItemList label={this.props.labels.functions} 
                                        view={"function"}
                                        context={this} valKey={"basic-functions"}
                                        value={this.props.serialized.functions} />
                                </div>
                            </div>
                            <hr />

                            <div className="row">
                                <div className="col-md-12">
                                    <label data-tip="Data Access Nodes reference GraphQL schemas. This section lists queries and mutations in each schema. Click + to upload a new schema."
                                    >
                                        {this.props.labels.data_access}
                                        <i className="fe icon-info"/>
                                    </label>
                                </div>
                                <div className="col-md-12">
                                    <ItemList label={this.props.labels.graphql_schemas} view={"schema"}
                                        context={this} valKey={"basic-graphQLSchemas"} key={this.state.graphQLKey}
                                        value={this.state.graphQLSchemas} />
                                </div>
                            </div>
                            <hr />

                            <div className="row">
                                <div className="col-md-12">
                                    <label
                                        data-tip="The CSV files currently uploaded for use by Tabular Router Nodes. This section lists each CSV file and shows the first three data rows in each file. Click on each file to edit it. Click + to upload a new file."
                                    >
                                        {this.props.labels.tabular_data}
                                        <i className="fe icon-info"/>
                                    </label>
                                </div>
                                <div className="col-md-12">
                                    <ItemList label={this.props.labels.csv_data} view={"csv"}
                                        context={this} valKey={"basic-csvData"} key={this.state.csvDataKey}
                                        value={this.state.csvData} />
                                </div>
                            </div>
                        </div>

                    }

                    {isCommonModule &&
                        <div id="basic" className="tab-pane active">
                            <div className="row">
                                <div className="col-md-6">
                                    <Textarea
                                        label={this.props.labels.description}
                                        tooltip="Write a short description of the module's purpose."
                                        context={this}
                                        valKey={"basic-description"}
                                        value={this.props.serialized.description}
                                    />
                                </div>
                                <div className="col-md-6 flex-gap">

                                    <SwitchWrapper
                                        label="Allow Disable Sensitive Data"
                                        defaultChecked={!!this.props.serialized.sensitiveOverrides.enabled}
                                        tooltip="Select this option to allow Application Designers to override sensitive data settings
                                            in the common module when they import it into an application.
                                            When imported, data marked as sensitive in the common module is no longer marked as sensitive in the application."
                                        inputFirst
                                        onChange={
                                            (e) => {
                                                window.apm.serialized.sensitiveOverrides.enabled = e.target.checked;
                                                window.apm.serialized.sensitiveOverrides.lastLocalChange = window.apm.timestamp;
                                                window.apm.setSerializedChanged();
                                            }
                                        }
                                    />

                                    <SwitchWrapper
                                        label="Allow Session Variable Access"
                                        defaultChecked={!!this.props.serialized.rootSettings.allowSessionVariableAccess}
                                        tooltip="This common module is used for a specific client and can access and/or modify session variables."
                                        inputFirst
                                        onChange={
                                            (e) => {
                                                window.apm.serialized.rootSettings.allowSessionVariableAccess = e.target.checked;
                                                window.apm.serialized.rootSettings.lastLocalChange = window.apm.timestamp;
                                                window.apm.setSerializedChanged();
                                            }
                                        }
                                    />                       
                                    
                                </div>
                            </div>
                            <hr />

                            <div className="row">
                                <div className="col-md-12">
                                    <label data-tip="Data Access Nodes reference GraphQL schemas. This section lists queries and mutations in each schema. Click + to upload a new schema.">
                                        {this.props.labels.data_access}
                                        <i className="fe icon-info"/>
                                    </label>
                                </div>
                                <div className="col-md-12">
                                    <ItemList label={this.props.labels.graphql_schemas} view={"schema"}
                                        context={this} valKey={"basic-graphQLSchemas"} key={this.state.graphQLKey}
                                        value={this.state.graphQLSchemas} />
                                </div>
                            </div>
                            <hr />
                            <div className="row">
                                <div className="col-md-12">
                                    <label data-tip="The CSV files currently uploaded for use by Tabular Router Nodes. This section lists each CSV file and shows the first three data rows in each file. Click on each file to edit it. Click + to upload a new file.">
                                        {this.props.labels.tabular_data}
                                        <i className="fe icon-info"/>
                                    </label>
                                </div>
                                <div className="col-md-12">
                                    <ItemList label={this.props.labels.csv_data} view={"csv"}
                                        context={this} valKey={"basic-csvData"} key={this.state.csvDataKey}
                                        value={this.state.csvData} />
                                </div>
                            </div>
                        </div>
                    }

                    <div id="properties" className="tab-pane fade">
                        <div className="row">
                            <div className="col-md-12">
                                <AppProperties
                                    properties={window.apm.serialized.appProperties}
                                />
                            </div>
                        </div>
                    </div>

                    <div id="locale" className="tab-pane fade">
                        <div className="row">
                            <div className="col-md-12">
                                <Locale
                                    context={this}
                                    valKey={"basic-languages"}
                                    serialized={this.props.serialized}
                                    referenceablePrompts={this.state.referenceablePrompts}
                                    key={this.state.referenceablePrompts}
                                    languages={this.state.languages}
                                    voice={this.state.voice}
                                />
                            </div>
                        </div>
                    </div>

                    {(isCommonModule) ? null : (
                        <div id="hau" className="tab-pane fade">
                            <div className="row">
                                <div className="col-md-12">
                                    <SettingsHauPage />
                                </div>
                            </div>
                        </div>
                    )}

                    {Utility.getIsCommonModule(window.apm.serialized) && wizardConfig&&
                        <div id="wizardConfig" className="tab-pane fade">
                        <div className="row">
                            <div className="col-md-12">
                                <CoMoWizardConfig
                                    context={this}
                                    appContext={window.apm}
                                    valKey={"wizardConfig"}
                                    wizardConfig={window.apm.serialized.wizardConfig}
                                />
                            </div>
                        </div>
                    </div>}

                    {!Utility.getIsCommonModule(window.apm.serialized) && <div id="filler_prompts" className="tab-pane fade">
                        <FillerPrompts prompts={this.props.serialized.fillerPrompts} />
                    </div>}

                    <div id="liveAgent" className="tab-pane fade">
                        <div className="row">
                            <div className="col-md-12">
                                <LiveAgent />
                            </div>
                        </div>
                    </div>
                    <div id="ccxml" className="tab-pane fade">
                        <div className="row">
                            <div className="col-md-12">
                                <CCXML context={this} onChange={this.props.serialized.ccxml} valKey={"ccxml"} value={this.props.serialized.ccxml} />
                            </div>
                        </div>
                    </div>
                    {/*TODO: Feature flag 894 begins*/}
                    {inventoryEnabled && <div id="about" className="tab-pane fade">
                        <div className="row">
                            <div className="col-md-12">
                                <About />
                            </div>
                        </div>
                    </div>}
                    {/*TODO: Feature flag 894 ends*/}
                </div>
            </div>
        );
    }
}
